'use strict';

angular.module('ClinicApp').factory('ClinicService', (
  $q,
  ClinicResource,
  ClinicDoctorsResource,
  PermissionResource,
  RequestPhoneVerificationResource,
  VerifyPhoneResource,
  ClaimClinicResource,
  VerifyPhoneTokenResource,
  ClinicSearchResource,
  CompanyClinicResource,
  ClinicFeatureResource,
  ClinicOCSResource,
  UserSignDocumentsResource,
  $http,
  ClinicConfig,
) => ({
  getAllClinicsId: () => 'all-clinics',

  claimAward: function(clinicId) {
    return ClinicResource.update({
      id: clinicId,
      action: 'claimAward',
    }, null).$promise;
  },

  verifyAward: function(clinicId, website) {
    return ClinicResource.update({
      id: clinicId,
      action: 'verifyAward',
    }, {
      website: website,
    }).$promise;
  },

  createClinic: function(companyId, data) {
    return CompanyClinicResource.save({
      id: companyId,
    }, data).$promise;
  },

  getClinic: function(clinicId, doctorInsurance) {
    var options = {
      id: clinicId,
      noDoctorInsurancePlans: true,
    };

    if (doctorInsurance) {
      delete options.noDoctorInsurancePlans;
    }

    return ClinicResource.get(options).$promise;
  },

  // Cheap, targeted lookup of just the clinic's AI assistant flag. Skips all the heavy
  // relations that ClinicResource.get normally populates, since callers here only need
  // clinic.aiSettings.assistantEnabled. Fails closed (resolves false) on any error, since
  // this is used to gate an informational UI element, not critical functionality.
  getClinicAiAssistantEnabled: async function(clinicId) {
    try {
      const clinic = await ClinicResource.get({
        id: clinicId,
        withAiSettings: true,
        noReviews: true,
        noCompany: true,
        noQuestionResponses: true,
        noProviders: true,
        noProviderOfferedAppointmentTypes: true,
        noProviderSpecialties: true,
        noProviderVacationAvailabilityBlocks: true,
        noProviderOnVacationNow: true,
        noPrimarySpecialty: true,
        noDoctorInsurancePlans: true,
        noFeatures: true,
        noSync: true,
        noCustomerSuccessManager: true,
        noCurrentStatus: true,
        noOcsMetadata: true,
      }).$promise;

      return Boolean(clinic?.aiSettings?.assistantEnabled);
    } catch {
      return false;
    }
  },

  getClinics: function() {
    return $http.get(`${ClinicConfig.HOSTNAME}/clinic/user`)
      .then((response) => response.data);
  },

  getOCSData: function(clinicId) {
    var options = {
      id: clinicId,
    };
    return ClinicOCSResource.get(options).$promise;
  },

  getClinicAward: function(clinicId) {
    return ClinicResource.get({
      id: clinicId,
      action: 'award',
    }).$promise;
  },

  getClinicDoctors: function(clinicId) {
    return ClinicDoctorsResource.query({
      id: clinicId,
    }).$promise;
  },

  getClinicPermissions: function(clinicId) {
    return PermissionResource.query({
      modelType: 'clinic',
      id: clinicId,
    }).$promise;
  },

  makePhoneVerificationCall: function(clinicId, phoneExtension) {
    var params = null;
    if (phoneExtension) {
      params = {
        phoneExtension: phoneExtension,
      };
    }
    return RequestPhoneVerificationResource.save({
      id: clinicId,
    }, params).$promise;
  },

  findWithPhone: function(phone) {
    if (!phone) {
      return $q.when([]);
    }
    return ClinicSearchResource.query({
      phone: phone,
    }).$promise;
  },

  removeClinic: function(clinicId) {
    return ClinicResource.remove({
      id: clinicId,
    }).$promise;
  },

  updateClinic: function(clinic, data) {
    if (typeof data !== 'object' || !Object.keys(data).length) {
      return $q.when();
    }
    return ClinicResource.update({
      id: clinic.id,
    }, {
      name: clinic.name,
      address: clinic.address,
      latitude: clinic.latitude,
      longitude: clinic.longitude,
      ...data,
    }).$promise;
  },

  updateFeatures: function(clinicId, featureIds) {
    return ClinicFeatureResource.update({
      id: clinicId,
      featureIds: featureIds,
    }).$promise;
  },

  claimClinic: function(companyId, clinicId, verificationCode) {
    return ClaimClinicResource.save({
      id: companyId,
      clinicId: clinicId,
    }, {
      phoneToken: {
        token: verificationCode,
      },
    }).$promise;
  },

  verifyPhone: function(clinicId, verificationToken) {
    if (!clinicId || !verificationToken) {
      return $q.reject('ClinicService.verifyPhone: clinicId and verificationToken are required.');
    }
    return VerifyPhoneResource.save({}, {
      phoneToken: {
        token: verificationToken,
      },
      clinicId: clinicId,
    }).$promise;
  },

  verifyPhoneToken: function(clinicId, token) {
    return VerifyPhoneTokenResource.save({}, {
      phoneToken: token,
      clinicId: clinicId,
    }).$promise;
  },

  analytics: function(clinicId) {
    return $http.get(ClinicConfig.HOSTNAME + '/clinic/analytics/' + clinicId).then(function(response) {
      return response.data;
    });
  },

  getPerformanceMetrics: (clinicIds, periods) => {
    return $http.get(`${ClinicConfig.HOSTNAME}/clinic/performance`, {
      params: {
        clinicIds: clinicIds.join(','),
        periods: periods.join(','),
      },
      cache: true,
    }).then((response) => response.data);
  },

  signDocuments: function(signedDocuments) {
    return UserSignDocumentsResource.update({}, {
      signedDocuments: signedDocuments,
    }).$promise;
  },

  sendBulkMessage: function(appointments, addRescheduleLink) {
    return $http.post(ClinicConfig.HOSTNAME + '/patient/appointment/bulkRescheduleMessage', {
      appointments: appointments,
      addRescheduleLink: addRescheduleLink,
    });
  },

  getMembership: (clinicId) => {
    return $http.get(`${ClinicConfig.HOSTNAME}/clinic/${clinicId}/membership`).then((response) => response.data || null);
  },

  getPerformanceWidgetData: (clinicId) =>
    $http.get(`${ClinicConfig.HOSTNAME}/clinic/${clinicId}/performanceWidgetData`)
      .then((response) => response.data || null),

  generateInstantBookMappings: (clinicId) =>
    $http.post(`${ClinicConfig.HOSTNAME}/clinic/${clinicId}/instantBook/generate`)
      .then((response) => response.data),

  getClinicRanking: (clinicId) => $http.get(`${ClinicConfig.HOSTNAME}/clinic/${clinicId}/ranking`)
    .then((response) => response.data || null),

  getOpenSlots: (clinicId) =>
    $http.get(`${ClinicConfig.HOSTNAME}/clinic/v2/?ids=${clinicId}`)
      .then((response) => response.data?.[0]?.slots || []),
}));
